import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Typography,
  Collapse,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  LocalShipping as SupplierIcon,
  // Security as SecurityIcon,
  // Newspaper as SchemeCardIcon,
  PendingActions as RoznamaIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  Assessment,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

const Sidebar = ({ isOpen = true, onToggle, mobileOpen = false, onMobileClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isCollapsed = !isOpen && !isMobile;

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  // Transparent glass background
  const glassBackground = isDarkMode
    ? 'rgba(15, 23, 42, 0.4)'
    : 'rgba(255, 255, 255, 0.4)';

  const glassBackgroundStrong = isDarkMode
    ? 'rgba(15, 23, 42, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  const colors = {
    light: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(37, 99, 235, 0.1)',
      chip: {
        background: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.2)',
        text: '#2563EB'
      }
    },
    dark: {
      primary: theme.palette.primary.main,
      primaryLight: theme.palette.primary.light,
      primaryDark: theme.palette.primary.dark,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(148, 163, 184, 0.1)',
      chip: {
        background: 'rgba(96, 165, 250, 0.1)',
        border: 'rgba(96, 165, 250, 0.2)',
        text: '#60A5FA'
      }
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  const mainMenuItems = [
    { label: "Dashboard", icon: DashboardIcon, path: "/", badge: null },
    {
      type: "dropdown",
      label: "Customers",
      icon: CustomersIcon,
      openState: customerOpen,
      setOpenState: setCustomerOpen,
      badge: "5",
      children: [
        { label: "View Customer", path: "/customers" },
        { label: "Add Customer Transaction", path: "/create-customer-transactions" },
        { label: "Customer Transaction History", path: "/customer-transactions" },
        { label: "Customer Ledger", path: "/customer-ledger" },
        { label: "Advance Payments", path: "/advance-payment" },
      ],
    },
    {
      type: "dropdown",
      label: "Suppliers",
      icon: SupplierIcon,
      openState: supplierOpen,
      setOpenState: setSupplierOpen,
      badge: "4",
      children: [
        { label: "View Supplier", path: "/supplier" },
        { label: "Add Supplier Transaction", path: "/supplier-transaction" },
        { label: "Supplier Transaction History", path: "/supplier-transaction-history" },
        { label: "Supplier Ledger", path: "/supplier-ledger" },
      ],
    },
    {
      type: "dropdown",
      label: "Stock",
      icon: Assessment,
      openState: stockOpen,
      setOpenState: setStockOpen,
      badge: "4",
      children: [
        { label: "Add Stock", path: "/addItem" },
        { label: "View Inventory", path: "/inventory" },
        { label: "Stock Transaction Report", path: "/StockTransactionReport" },
        { label: "Stock Verification", path: "/stock-verification" },
      ],
    },
    { label: "Roznama", icon: RoznamaIcon, path: "/roznama", badge: null },
    { label: "Products Gallery", icon: Assessment, path: "/products", badge: null },
  ];

  const isActive = (path) => activeItem === path;

  const handleNavigate = (path) => {
    navigate(path);
    setActiveItem(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleToggleCollapse = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const sidebarContent = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1.5,
          borderBottom: `1px solid ${currentColors.border}`,
        }}
      >
        {!isCollapsed && !isMobile && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              color: currentColors.textSecondary,
              opacity: 0.7,
            }}
          >
            Navigation
          </Typography>
        )}
        {!isMobile && (
          <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
            <IconButton
              size="small"
              onClick={handleToggleCollapse}
              sx={{
                marginLeft: isCollapsed ? 0 : "auto",
                borderRadius: 2,
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
                border: `1px solid ${currentColors.border}`,
                color: currentColors.textPrimary,
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                  transform: "scale(1.1)",
                  color: currentColors.primary,
                },
              }}
            >
              {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 20 }} /> : <ChevronLeftIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List sx={{ flex: 1, pt: 2.5 }}>
        {mainMenuItems.map((item, index) => {
          if (item.type === "dropdown") {
            const Icon = item.icon;
            const isActiveChild = item.children.some((child) => activeItem === child.path);
            const showCollapsed = isMobile ? false : isCollapsed;

            return (
              <React.Fragment key={index}>
                <Tooltip title={showCollapsed ? item.label : ""} placement="right" arrow>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => item.setOpenState(!item.openState)}
                      selected={isActiveChild}
                      sx={{
                        borderRadius: 2.5,
                        px: showCollapsed ? 1 : 1,
                        py: { xs: 1.2, sm: 1.5 },
                        minHeight: { xs: 44, sm: 52 },
                        backgroundColor: isActiveChild
                          ? isDarkMode ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isDarkMode
                            ? "rgba(96, 165, 250, 0.08)"
                            : "rgba(37, 99, 235, 0.05)",
                          transform: { xs: "none", sm: "translateX(4px)" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: showCollapsed ? 0 : { xs: 40, sm: 44 }, justifyContent: "center" }}>
                        <Icon sx={{ fontSize: { xs: 22, sm: 26 }, color: isActiveChild ? currentColors.primary : currentColors.textSecondary }} />
                      </ListItemIcon>
                      {!showCollapsed && !isMobile && (
                        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: isActiveChild ? 700 : 600, fontSize: { xs: "0.85rem", sm: "0.95rem" }, color: isActiveChild ? currentColors.primary : currentColors.textPrimary } }} />
                            {item.badge && <Chip label={item.badge} size="small" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: "0.6rem", sm: "0.7rem" }, fontWeight: 700, backgroundColor: currentColors.chip.background, color: isActiveChild ? currentColors.primary : currentColors.chip.text, border: `1px solid ${currentColors.chip.border}`, borderRadius: '8px' }} />}
                          </Box>
                          <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 20 }, transform: item.openState ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease", color: isActiveChild ? currentColors.primary : currentColors.textSecondary }} />
                        </Box>
                      )}
                      {isMobile && !showCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: isActiveChild ? 700 : 600, fontSize: "0.85rem", color: isActiveChild ? currentColors.primary : currentColors.textPrimary } }} />}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
                {(!showCollapsed || isMobile) && (
                  <Collapse in={item.openState} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: { xs: 1, sm: 2 }, pr: 1 }}>
                      {item.children.map((child) => (
                        <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton onClick={() => handleNavigate(child.path)} selected={isActive(child.path)} sx={{ borderRadius: 2, px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.2 }, minHeight: { xs: 38, sm: 42 } }}>
                            <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 }, justifyContent: "center" }}>
                              <CircleIcon sx={{ fontSize: { xs: 6, sm: 8 }, color: isActive(child.path) ? currentColors.primary : currentColors.textSecondary }} />
                            </ListItemIcon>
                            <ListItemText primary={child.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: isActive(child.path) ? 600 : 500, fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: isActive(child.path) ? currentColors.primary : currentColors.textSecondary } }} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          } else {
            const Icon = item.icon;
            const active = isActive(item.path);
            const showCollapsed = isMobile ? false : isCollapsed;

            return (
              <Tooltip key={item.path} title={showCollapsed ? item.label : ""} placement="right" arrow>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton onClick={() => handleNavigate(item.path)} selected={active} sx={{ borderRadius: 2.5, px: showCollapsed ? 1 : 1, py: { xs: 1.2, sm: 1.5 }, minHeight: { xs: 44, sm: 52 }, backgroundColor: active ? (isDarkMode ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)") : "transparent", "&:hover": { backgroundColor: isDarkMode ? "rgba(96, 165, 250, 0.08)" : "rgba(37, 99, 235, 0.05)", transform: { xs: "none", sm: "translateX(4px)" } } }}>
                    <ListItemIcon sx={{ minWidth: showCollapsed ? 0 : { xs: 40, sm: 44 }, justifyContent: "center" }}>
                      <Icon sx={{ fontSize: { xs: 20, sm: 22 }, color: active ? currentColors.primary : currentColors.textSecondary }} />
                    </ListItemIcon>
                    {!showCollapsed && !isMobile && <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: active ? 700 : 600, fontSize: { xs: "0.85rem", sm: "0.95rem" }, color: active ? currentColors.primary : currentColors.textPrimary } }} />}
                    {isMobile && !showCollapsed && <ListItemText primary={item.label} primaryTypographyProps={{ variant: "body2", sx: { fontWeight: active ? 700 : 600, fontSize: "0.85rem", color: active ? currentColors.primary : currentColors.textPrimary } }} />}
                    {showCollapsed && active && <Box sx={{ position: "absolute", right: 12, width: { xs: 4, sm: 6 }, height: { xs: 4, sm: 6 }, borderRadius: "50%", background: currentColors.primary, boxShadow: `0 0 8px ${currentColors.primary}40` }} />}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          }
        })}
      </List>

      {(!isCollapsed || isMobile) && (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderTop: `1px solid ${currentColors.border}`, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "0.9rem", sm: "1rem" }, mb: 0.5, color: currentColors.primary }}>JewelStock</Typography>
          <Typography variant="caption" sx={{ color: currentColors.textSecondary, display: "block", fontSize: { xs: "0.6rem", sm: "0.7rem" }, fontWeight: 500 }}>Version 1.0.0</Typography>
        </Box>
      )}
    </>
  );

  // For mobile - show drawer when mobileOpen is true
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            background: glassBackgroundStrong,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRight: `1px solid ${currentColors.border}`,
            boxShadow: "none",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // For desktop - use permanent drawer
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        flexShrink: 0,
        display: { xs: "none", md: "block" },
        "& .MuiDrawer-paper": {
          width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          boxSizing: "border-box",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowX: "hidden",
          background: glassBackground,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRight: `1px solid ${currentColors.border}`,
          boxShadow: "none",
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;